import { isWeb, SizableText, XStack, useMedia } from '@mythical/ui'
import { Meta } from 'app/components/primitive/Meta'
import MainLayout from 'app/components/structure/Layout/MainLayout'
import usePagination from 'app/hooks/use-pagination'
import { contentListQueryToContentList } from 'app/queries/content/all-content-list'
import { createSearchQuery } from 'app/queries/search/search-content'
import { useStores } from 'app/store'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useCallback } from 'react'
import { Button } from 'app/components/primitive/Button'
import { FlatListGrid } from 'app/components/primitive/Grids'
import { ContentCardBookmarkable } from 'app/components/content/ContentCard/ContentCardBookmarkable'
import { ContentCardProps } from 'app/components/content/ContentCard'
import { useSearchContext } from 'app/components/structure/Search/SearchContext'
import Search from 'app/components/structure/Search'
import { FullScreenOverlay } from 'app/components/primitive/FullScreenOverlay'
import Spinner from 'app/components/primitive/Spinner'
import { Container } from 'app/components/structure/Container'
import {
  WatchLaterProvider,
  useWatchLaterContext,
} from 'app/providers/WatchLaterProvider'

const config = {
  formatFn: contentListQueryToContentList,
  limit: 18,
}

const query = createSearchQuery()

interface SearchResultsGridProps {
  docs: ContentCardProps[]
  hasMore: boolean
  onLoadMore: () => void
  isFetchingNextPage: boolean
}

const SearchResultsGrid = ({
  docs,
  hasMore,
  onLoadMore,
  isFetchingNextPage,
}: SearchResultsGridProps) => {
  const { isInWatchLater, toggleWatchLater } = useWatchLaterContext()

  const renderItem = useCallback(
    (item: ContentCardProps) => (
      <ContentCardBookmarkable
        {...item}
        isInWatchLater={isInWatchLater(item.id)}
        onToggleWatchLater={toggleWatchLater}
        sizes="(min-width: 800px) 33vw, (min-width: 660px) 50vw, 100vw"
      />
    ),
    [isInWatchLater, toggleWatchLater],
  )

  const listFooterComponent = useMemo(
    () => (
      <XStack jc="center" ai="center" pt="$5" pb="$7">
        {hasMore && (
          <Button onPress={onLoadMore} loading={isFetchingNextPage} uppercase>
            Load More
          </Button>
        )}
      </XStack>
    ),
    [hasMore, onLoadMore, isFetchingNextPage],
  )

  const keyExtractor = useCallback((item: ContentCardProps) => item.id, [])

  const wrapperProps = useMemo(() => ({ overflow: 'hidden' as const }), [])

  const gridOptions = useMemo(
    () => ({
      $gtXs: { numColumns: 2 },
      $gtSm: { numColumns: 3 },
      $gtMd: { columnGap: 16, rowGap: 24 },
    }),
    [],
  )

  const flatListProps = useMemo(
    () => ({
      initialNumToRender: 12,
      removeClippedSubviews: true,
    }),
    [],
  )

  return (
    <FlatListGrid
      items={docs}
      keyExtractor={keyExtractor}
      wrapperProps={wrapperProps}
      options={gridOptions}
      renderItem={renderItem}
      ListFooterComponent={listFooterComponent}
      flatListProps={flatListProps}
    />
  )
}

export const ContentSearchScreen = observer(() => {
  const { lastCollectionStore } = useStores()
  const media = useMedia()
  const filters = useSearchContext((state) => state.filters)
  const searchResults = usePagination(query, filters, config)

  useEffect(() => {
    if (lastCollectionStore) {
      return lastCollectionStore.reset()
    }
  }, [lastCollectionStore])

  const docs = searchResults.data?.docs || []
  const contentIds = useMemo(() => docs.map((item) => item.id), [docs])

  return (
    <MainLayout
      noScroll={!isWeb}
      paddedBottom={false}
      contentStyles={{ fs: 1, fg: 1 }}
    >
      <Meta title="Search" slug="search" />
      <Container variant="none" fs={1} fg={1} jc="flex-start" paddedX>
        {media.md && (
          <Search pb="$3.5" $gtMd={{ dsp: 'none' }} id="mobile-search" />
        )}

        <WatchLaterProvider contentIds={contentIds}>
          <SearchResultsGrid
            docs={docs}
            hasMore={searchResults.hasMore ?? false}
            onLoadMore={searchResults.fetchMore}
            isFetchingNextPage={searchResults.isFetchingNextPage}
          />
        </WatchLaterProvider>

        {searchResults.loading && (
          <FullScreenOverlay asOverlay={false}>
            <Spinner size="large" />
          </FullScreenOverlay>
        )}

        {!searchResults.loading && docs?.length === 0 && (
          <FullScreenOverlay asOverlay={false}>
            <SizableText
              textAlign="center"
              textTransform="uppercase"
              color="$fgSecondary"
            >
              No content found
            </SizableText>
          </FullScreenOverlay>
        )}
      </Container>
    </MainLayout>
  )
})
